import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import React, {useState} from 'react';
import type {SourceEntityRecord} from 'src/api/ProactApiClient';
import {
  EntityTypeEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {Paginator} from 'src/design/organisms/Pagination';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';

export interface OfflineEmployeeListProps {
  selected: SourceEntityRecord[];
  setSelected: React.Dispatch<React.SetStateAction<SourceEntityRecord[]>>;
  showOnlySelected: boolean;
  showSelectedAll: boolean;
  search?: string;
  itemsPerPage?: number;
  entityType?: EntityTypeEnum;
}

/**
 * Offline stand-in for `EmployeeList`: the same selection contract served by
 * the bundled personnel section, which carries names only — no job title,
 * department or supervisor to show.
 */
export const OfflineEmployeeList: React.FC<OfflineEmployeeListProps> = ({
  selected,
  setSelected,
  showOnlySelected,
  showSelectedAll,
  search,
  itemsPerPage = 25,
  entityType = EntityTypeEnum.Employee,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const selectedIds = new Set(selected.map(item => Number(item.entityId)));

  const {data, isFetching, isLoading} = useGetPersonnelSelectOptionsQuery({
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    active: [UserStatusEnum.Active],
    superviserOnly: false,
    search,
    start: currentPage * itemsPerPage - itemsPerPage,
    length: itemsPerPage,
  });

  const rows = (data?.data ?? []).filter(
    row => !showOnlySelected || selectedIds.has(row.id)
  );

  const handleSelect = (checked: boolean, id: number) =>
    setSelected(
      checked
        ? [...selected, {entityId: id, entityType} as SourceEntityRecord]
        : selected.filter(item => Number(item.entityId) !== id)
    );

  if (isFetching || isLoading) {
    return (
      <>
        <TableBodyRender isLoading />
        <Space mb="xl" />
      </>
    );
  }

  return (
    <Box>
      <Paginator
        mt={8}
        mb={16}
        currentPage={currentPage}
        totalRecords={data?.filteredRecords ?? 0}
        perPage={itemsPerPage}
        showRecordCount={false}
        onChange={(_, page) => setCurrentPage(page)}
      />
      {rows.length > 0 ? (
        rows.map(row => (
          <CheckboxRow
            key={row.id}
            id={String(row.id)}
            label={row.label ?? ''}
            checked={selectedIds.has(row.id) || showSelectedAll}
            disabled={showSelectedAll}
            onChange={event => handleSelect(event.target.checked, row.id)}
          />
        ))
      ) : (
        <Box mt="lg">No selections found</Box>
      )}
    </Box>
  );
};
